import {
  Component,
  Output,
  EventEmitter,
  OnDestroy,
  OnInit,
  OnChanges,
  AfterViewInit,
  Input,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { DsButtonVariants, Icons } from '@levelaccess/design-system';
import { FormGroup, FormControl, FormBuilder } from '@angular/forms';
import { ChatInputContextChip, DsChatInputComponent } from '@levelaccess/design-system';
import { Observable, Subscription, combineLatest, Subject } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, startWith } from 'rxjs/operators';
import { InputFieldContext } from '@client/app/components/common-input/common-input.component';
import { CustomValidators } from '@client/app/services/helpers/form-custom-validators';
import { AiChatStatus } from '../ai-chat-base/ai-chat.interface';
import { ChatContextRegistry } from '../../../services/chat-context-registry.service';
import { NotificationService } from '../../../services/notification.service';
import { NotificationPosition } from '../../../models/notification.model';
import { TranslateService } from '../../../translate/translate.service';
import { FeatureFlagService } from '../../../services/feature-flag/feature-flag.service';
import { FeatureFlagCollection } from '../../../../../shared/interfaces/feature-flag.interface';
import { RoutingService } from '../../../services/routing.service';
import { AiChatStateService } from '../ai-chat-state.service';

const CONTEXT_CHIP_ID: string = 'context';

@Component({
  selector: 'app-ai-chat-footer',
  templateUrl: './ai-chat-footer.component.html',
  styleUrls: ['./ai-chat-footer.component.scss'],
  standalone: false,
})
export class AiChatFooterComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() status: AiChatStatus = AiChatStatus.IDLE;
  @Input() messageValue: string = '';
  @Input() focusInputRequest$: Subject<void>;
  @Input() clearInputRequest$: Subject<void>;
  @Output() sendMessage: EventEmitter<string> = new EventEmitter<string>();

  @ViewChild('chatInput') private chatInputRef?: DsChatInputComponent;

  protected form: FormGroup<{ message: FormControl }>;
  protected formValidationRequest$: Subject<void> = new Subject<void>();
  protected readonly inputContext: InputFieldContext;
  protected readonly AiChatStatus: typeof AiChatStatus = AiChatStatus;
  protected readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  protected readonly Icons: typeof Icons = Icons;
  protected contextChips$: Observable<ChatInputContextChip[]>;

  // Gates which input UI renders: the new ds-chat-input when on, the legacy form when off.
  protected aiChatAddContext$: Observable<boolean>;

  private _isAddContextEnabled: boolean = false;
  private _subscription: Subscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    private elementRef: ElementRef,
    private chatContextRegistry: ChatContextRegistry,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private featureFlagService: FeatureFlagService,
    private routingService: RoutingService,
    private aiChatStateService: AiChatStateService,
  ) {
    this.inputContext = {
      label: 'ai_chat_input_label',
      field: 'message',
      placeholder: 'ai_chat_input_placeholder',
      required: false,
      validateOnSubmitOnly: true,
      id: 'ai-chat-input',
      type: 'text',
      noLabel: true,
    };
    this.form = this.fb.group({
      message: this.fb.control('', [CustomValidators.validateIsEmpty]),
    });
  }

  ngOnInit(): void {
    this.aiChatAddContext$ = this.featureFlagService
      .variation$(FeatureFlagCollection.aiChatAddContext, false)
      .pipe(map(Boolean), startWith(false), distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: true }));

    const showContextChip$ = combineLatest([this.aiChatAddContext$, this.chatContextRegistry.dismissed$]).pipe(
      map(([isEnabled, dismissed]: [boolean, boolean]): boolean => isEnabled && !dismissed),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.contextChips$ = combineLatest([showContextChip$, this.routingService.pageTitle$]).pipe(
      map(([showChip, pageTitle]: [boolean, string]) => this.getContextChips(showChip, pageTitle)),
    );

    this._subscription.add(this.aiChatAddContext$.subscribe((isEnabled: boolean) => (this._isAddContextEnabled = isEnabled)));

    this._subscription.add(
      this.form.get('message').valueChanges.subscribe((value: string) => {
        this.updateSuggestionsDisabledState(value);
        this.aiChatStateService.setDraftMessage(value);
      }),
    );

    this.focusInput();
    if (this.focusInputRequest$) {
      this._subscription.add(this.focusInputRequest$.subscribe(() => this.focusInput()));
    }
    if (this.clearInputRequest$) {
      this._subscription.add(this.clearInputRequest$.subscribe(() => this.clearInput()));
    }
  }

  private clearInput(): void {
    this.form.reset();
    if (this.chatInputRef) {
      this.chatInputRef.value = '';
    }
    this.aiChatStateService.setDraftMessage('');
  }

  ngAfterViewInit(): void {
    const draftMessage: string = this.aiChatStateService.getDraftMessage();
    if (!draftMessage) {
      return;
    }

    if (this.chatInputRef) {
      setTimeout(() => {
        if (this.chatInputRef) {
          this.chatInputRef.value = draftMessage;
          this.updateSuggestionsDisabledState(draftMessage);
        }
      });
    } else if (this.form) {
      this.form.get('message').setValue(draftMessage);
    }
  }

  ngOnChanges(): void {
    if (!this.messageValue) {
      return;
    }

    if (this._isAddContextEnabled) {
      if (this.chatInputRef) {
        this.chatInputRef.value = this.messageValue;
      }
      this.updateSuggestionsDisabledState(this.messageValue);
      this.aiChatStateService.setDraftMessage(this.messageValue);
    } else if (this.form) {
      this.form.get('message').setValue(this.messageValue);
    }
  }

  ngOnDestroy(): void {
    this.aiChatStateService.setSuggestionsDisabled(false);
    this._subscription.unsubscribe();
  }

  public onSendMessageOld(): void {
    this.formValidationRequest$.next();
    if (!this.form.valid) {
      // Focus immediately (no debounce) so keyboard/screen-reader users land on the
      // invalid field as soon as the validation error renders, unlike the 500ms
      // default used for less time-sensitive focus restores elsewhere.
      this.focusInput(0);
      return;
    }
    this.sendMessage.emit(this.form.value.message);
    this.form.reset();
    this.aiChatStateService.setDraftMessage('');
  }

  public onHandleEnterOld(event: KeyboardEvent): void {
    if (this.status !== AiChatStatus.IDLE) {
      event.preventDefault();
    } else if (!event.shiftKey) {
      event.preventDefault();
      this.onSendMessageOld();
    }
  }

  public onSendMessage(message: string): void {
    this.sendMessage.emit(message);
    this.aiChatStateService.setSuggestionsDisabled(false);
    this.aiChatStateService.setDraftMessage('');
  }

  public onChatInputNativeInput(event: Event): void {
    const value: string = (event.target as HTMLTextAreaElement).value;
    this.updateSuggestionsDisabledState(value);
    this.aiChatStateService.setDraftMessage(value);
  }

  private updateSuggestionsDisabledState(value: string): void {
    const isPromptInputChangeEnabled = this.featureFlagService.variation(FeatureFlagCollection.aiChatPromptInputChange, false);

    if (!isPromptInputChangeEnabled) {
      return;
    }

    this.aiChatStateService.setSuggestionsDisabled(Boolean(value?.trim()));
  }

  public onDismissChip(): void {
    this.chatContextRegistry.dismissContext();

    this.notificationService.success(
      this.translateService.instant('ai_chat_context_removed_body'),
      NotificationPosition.Toast,
      true,
      undefined,
      undefined,
      undefined,
      () => this.focusInput(0),
    );
  }

  public onRestoreContext(): void {
    this.chatContextRegistry.restoreAllContext();
  }

  protected getContextChips(showChip: boolean, pageTitle: string): ChatInputContextChip[] {
    if (!showChip) {
      return [];
    }

    return [
      {
        id: CONTEXT_CHIP_ID,
        title: this.translateService.instant('ai_chat_context_chip_title'),
        value: pageTitle || this.translateService.instant('ai_chat_context_chip_value'),
      },
    ];
  }

  private focusInput(delay: number = 500): void {
    setTimeout(() => {
      const inputElement: HTMLElement = this.elementRef.nativeElement.querySelector('input, #ds-chat-input-textarea');
      inputElement?.focus();
    }, delay);
  }
}
